import { animationEnabled, useDashboardTheme } from "components/dashboard";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import getCustomLegend from "utils/ChartHelpers.js/CustomLegendPlugin";
import { cn } from "utils/common.utils";

/**
 * Custom Hook to generate Chart.js Bar options based on mode and container ID.
 * This hook generates options immutably and memoizes the result for performance.
 *
 * @param {string} containerId - ID of the container element where the custom HTML legend will be placed.
 * @returns {object} The complete Chart.js options object.
 */
export function useBarChart(containerId) {
  // Use useMemo to ensure the options object is only re-created
  // when the dependencies (containerId) change.
  const options = useMemo(() => {
    // --- 1. Define Base Configuration ---
    const baseCommonOptions = {
      maintainAspectRatio: false,
      responsive: true,
      animation: animationEnabled,
      scales: {
        x: {
          stacked: true,
        },
        y: {
          stacked: true,
          ticks: {
            stepSize: 400000,
            display: false,
          },
        },
      },
    };

    const basePlugins = {
      legend: {
        display: false,
      },
      htmlLegend: {
        // ID of the container to put the legend in
        containerID: containerId,
      },
      tooltip: {
        mode: "index",
        interaction: { intersect: false },
      },
    };

    // --- 2. Create Initial Options Object (Full Copy) ---
    let finalOptions = {
      ...baseCommonOptions,
      plugins: { ...basePlugins },
    };

    return finalOptions;
  }, [containerId]);

  const { isDefaultStyle, primaryColor, primaryColor_05 } = useDashboardTheme();
  const successColorCode = isDefaultStyle ? "#77D3E1" : primaryColor;
  const failureColorCode = isDefaultStyle ? "#706CF9" : primaryColor_05;
  return {
    BarChart: ({ data }) => {
      const modifiedData = {
        ...data,
        datasets: data.datasets.map((ds) => {
          return {
            ...ds,
            backgroundColor: ds.backgroundColor ?? successColorCode,
            borderColor: ds.borderColor ?? successColorCode,
            borderWidth: ds.borderWidth ?? 0,
            barThickness: ds.barThickness ?? 20,
          };
        }),
      };
      return (
        <Bar data={modifiedData} options={options} plugins={[getCustomLegend()]} />
      );
    },
    BarChartFooter: ({ className }) => (
      <div id={containerId} className={cn("mt-8", className)} />
    ),
  };
}
